<template>
	<div class="control-bar">
		<div ref="scrollRef" class="control-scroll">
			<div class="control-group control-group-left">
				<!-- TTS：開關 TTS 視窗，開著時整顆轉藍；頻道或語言不支援時不給 -->
				<button v-if="ttsAvailable" id="tts-button" class="control-item" :class="{ 'is-active': showTts }"
						type="button" :aria-pressed="showTts" @click="emit('tts')">
					<span class="control-icon" aria-hidden="true" v-html="icons[showTts ? 'tts' : 'tts-off']" />
					<span class="control-label">{{ t('Control.Tts') }}</span>
				</button>
				<!-- Original：開關原文視窗，眼睛開合已經把狀態說清楚，不再轉色 -->
				<button id="source-button" class="control-item" type="button" :aria-pressed="showOrigin"
						@click="emit('original')">
					<span class="control-icon" aria-hidden="true" v-html="icons[showOrigin ? 'eye' : 'eye-off']" />
					<span class="control-label">{{ t('Control.Original') }}</span>
				</button>
			</div>
			<span class="control-divider"></span>
			<div class="control-group control-group-right">
				<!-- Spacing：不是開關，是叫出段距彈窗的入口 -->
				<button id="line-spacing-button" class="control-item" :class="{ 'is-open': spacingOpen }"
						type="button" aria-haspopup="true" :aria-expanded="spacingOpen" @click="emit('spacing')">
					<span class="control-icon" aria-hidden="true" v-html="icons['line-spacing']" />
					<span class="control-label">{{ t('Control.Spacing') }}</span>
				</button>
				<!-- Text-size：同上，叫出字級彈窗 -->
				<button id="scale-text-button" class="control-item" :class="{ 'is-open': textSizeOpen }"
						type="button" aria-haspopup="true" :aria-expanded="textSizeOpen" @click="emit('textSize')">
					<span class="control-icon" aria-hidden="true" v-html="icons['textsize']" />
					<span class="control-label">{{ t('Control.TextSize') }}</span>
				</button>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { onBeforeUnmount, onMounted, onUpdated, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { icons } from '@shared-assets/icons-v2'

/**
 * 底部控制列：TTS、Original、Spacing、Text-size 四顆等寬項目
 */
defineProps<{
	/**
	 * TTS 視窗是否開著
	 */
	showTts: boolean
	/**
	 * 頻道與目標語言是否支援 TTS，不支援就不顯示 TTS 項目
	 */
	ttsAvailable: boolean
	/**
	 * 原文視窗是否開著
	 */
	showOrigin: boolean
	/**
	 * 段距彈窗是否開著
	 */
	spacingOpen: boolean
	/**
	 * 字級彈窗是否開著
	 */
	textSizeOpen: boolean
}>()

const emit = defineEmits<{
	tts: []
	original: []
	spacing: []
	textSize: []
}>()

/** 取得 i18n */
const { t } = useI18n()

/** 控制列捲動容器 */
const scrollRef = ref<HTMLElement | null>(null)
/** 尺寸變化觀察器 */
let resizeObserver: ResizeObserver | null = null

/**
 * 放不下才退回可橫向捲動的單列並顯示分隔線；
 * 先把 class 拿掉再量，確保量到的是沒有分隔線時的寬度，所以直接動 classList 不走 Vue 的 class 綁定
 * @return {void}
 */
const syncOverflow = (): void => {
	// 還沒掛載就不量
	const node = scrollRef.value
	if (!node) {
		return
	}

	// 先還原再量
	node.classList.remove('is-overflowing')
	// 內容比容器寬就標記為擠不下
	if (node.scrollWidth > node.clientWidth + 1) {
		node.classList.add('is-overflowing')
	}
}

// 掛載後量一次，並跟著視窗與容器尺寸重量
onMounted(() => {
	syncOverflow()
	window.addEventListener('resize', syncOverflow)
	// 環境沒有 ResizeObserver 時只靠視窗縮放與更新時重量
	if (typeof ResizeObserver !== 'undefined' && scrollRef.value) {
		resizeObserver = new ResizeObserver(syncOverflow)
		resizeObserver.observe(scrollRef.value)
	}
})

// 項目增減（TTS 可用性變了）或標籤換語言後重量
onUpdated(syncOverflow)

// 卸載時移除監聽
onBeforeUnmount(() => {
	window.removeEventListener('resize', syncOverflow)
	resizeObserver?.disconnect()
})
</script>
<style scoped lang="scss">
@use '@shared-assets/scss/variables' as *;

.control-bar {
	flex-shrink: 0;
	position: relative;
}

.control-scroll {
	// 放得下時用 grid：左右兩條 1fr 等寬，中間那條真的置中，右組靠最右
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	gap: 12px;
	// 左右留白做成內距，留白才會跟著內容一起捲走；取 max(16px, 置中所需的留白)，再往內 4 因為項目本身有視覺留白
	padding: var(--control-margin-y) calc(max(16px, (100% - var(--content-max-width)) / 2) + 4px);
	width: 100%;
	overflow-x: auto;
	overflow-y: hidden;
	scrollbar-width: none;
	// 手勢只吃水平，垂直滑動不會變成捲頁；捲到頭也不傳給外層
	overscroll-behavior-x: contain;
	touch-action: pan-x pinch-zoom;

	&::-webkit-scrollbar {
		display: none;
	}

	// 擠不下就退回可捲的單列，這時置中沒有意義
	&.is-overflowing {
		display: flex;
		justify-content: flex-start;
	}
}

// 手機寬度不置中，膠囊靠左比較好按
@media (max-width: #{$breakpoint-sm - 1}) {
	.control-scroll {
		display: flex;
		justify-content: space-between;
	}
}

.control-group {
	display: flex;
	align-items: center;
	// 組內 0：按鈕等寬、彼此相鄰，hover 的底色矩形之間沒有沒有回饋的縫
	gap: 0;
	flex-shrink: 0;
}

.control-group-left {
	grid-column: 2;
	justify-self: center;
}

.control-group-right {
	grid-column: 3;
	justify-self: end;
}

// 分隔線只有擠成一條可捲工具列時才出現
.control-divider {
	display: none;
	flex-shrink: 0;
	width: 1px;
	height: 36px;
	background: var(--ui-grey-lighter);
}

.control-scroll.is-overflowing .control-divider {
	display: block;
}

// 單一控制項：直式、icon 在上標籤在下，四顆等寬
.control-item {
	flex-shrink: 0;
	width: var(--control-item-width);
	padding: var(--control-item-padding);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: var(--control-icon-gap);
	background: none;
	border: none;
	// 40 高落在圓角 8 這一階
	border-radius: var(--radius-header-button);
	font-family: inherit;
	cursor: pointer;
	color: var(--text-body-01);
	transition: background-color 0.15s ease;

	// hover 與 pressed 只用底色，顏色已經被開關狀態用掉了
	&:hover {
		background: rgba(21, 21, 21, 0.06);
	}

	// 按著不放，以及自己的彈窗開著時維持被按住的樣子
	&:active,
	&.is-open {
		background: rgba(21, 21, 21, 0.12);
	}

	// 只有 TTS 開著時轉藍
	&.is-active {
		color: var(--translation);
	}
}

.control-icon {
	display: flex;
	width: 24px;
	height: 24px;
	flex-shrink: 0;

	:deep(svg) {
		width: 100%;
		height: 100%;
	}
}

.control-label {
	font-size: var(--text-control-label-size);
	line-height: 1.2;
	// 按鈕寫死 52，標籤給 8 的容錯：60 以內維持一行，超過才換行，沒有空格的長字靠 overflow-wrap 斷開
	width: calc(100% + 8px);
	margin: 0 -4px;
	white-space: normal;
	overflow-wrap: break-word;
	text-align: center;
}

@supports (text-box-trim: trim-both) {
	.control-label {
		text-box-trim: trim-both;
		text-box-edge: cap alphabetic;
	}
}
</style>
